/**
 * Sortable Quest Card
 * Draggable quest card component using dnd-kit
 * Displays quest info and allows drag-to-reorder
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { QuestDefinition } from '@/types';
import { customQuestManager } from '@/systems';

interface SortableQuestCardProps {
  quest: QuestDefinition;
  isSelected: boolean;
  onSelect: (questId: string) => void;
}

export function SortableQuestCard({ quest, isSelected, onSelect }: SortableQuestCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: quest.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCustom = customQuestManager.isCustomQuest(quest.id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onSelect(quest.id)}
      className={`
        p-3 mb-2 rounded cursor-grab active:cursor-grabbing border-2 transition-colors
        ${isSelected ? 'border-blue-500 bg-blue-900/20' : 'border-gray-700 bg-gray-800'}
        hover:border-gray-600
        ${isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''}
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {quest.isTutorial && <span className="text-yellow-400">⭐</span>}
            {quest.icon && <span>{quest.icon}</span>}
            <span className="font-bold text-white">{quest.name}</span>
          </div>
          <div className="text-xs text-gray-400 mt-1 line-clamp-2">
            {quest.description.length > 60
              ? `${quest.description.slice(0, 60)}...`
              : quest.description}
          </div>
          <div className="text-xs text-gray-500 mt-1 font-mono">
            [{quest.id}]
          </div>
        </div>
        {isCustom && (
          <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded font-semibold ml-2 shrink-0">
            Custom
          </span>
        )}
      </div>
    </div>
  );
}
